import * as React from "react"
import { useSearchParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  FileSpreadsheet,
  Upload,
} from "lucide-react"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { cargar, descargarEjemplo, fetchPlantillas, previsualizar } from "@/routes/carga/api"
import {
  erroresDelRechazo,
  huellaDeArchivo,
  lineasDelResultado,
  nombreDelSeparador,
  primerosErrores,
  puedeCargar,
  titularDelResultado,
  type ErrorDeFila,
  type Plantilla,
  type Previsualizacion,
  type ResultadoCarga,
} from "@/routes/carga/mapping"
import { t } from "@/lib/i18n"

/**
 * CARGA POR ARCHIVO. Una pantalla para las siete plantillas.
 *
 * POR QUE UNA SOLA. Siete pantallas de importacion son siete sitios donde
 * arreglar el mismo fallo y seis que se quedan viejos. El formato no lo sabe
 * esta pantalla: lo declara el backend en `/carga/plantillas` —columnas,
 * obligatorias, alias, opciones— y de ahi salen tanto la ayuda como el libro de
 * ejemplo que el cliente se descarga. Escribir aqui la lista de columnas seria
 * duplicar la declaracion y desincronizarla en el primer cambio.
 *
 * SON DOS PASOS, Y ESE ES EL PRODUCTO. Previsualizar no escribe nada; dice
 * cuantas filas se crearian, cuantas se actualizarian y que esta mal CON SU
 * NUMERO DE LINEA. Recien despues se escribe, y con motivo.
 *
 * LA TRAMPA QUE ESTA PANTALLA TIENE QUE EVITAR. Si alguien previsualiza,
 * corrige el archivo en Excel, vuelve a elegirlo y pulsa «Cargar», lo que hay en
 * pantalla describe el archivo ANTERIOR. Se escribiria algo que nadie reviso y
 * nada fallaria. Por eso la previsualizacion va atada a la huella del archivo
 * (nombre, tamaño y fecha de modificacion) y se invalida sola: ver
 * `puedeCargar` en `mapping.ts`, que es donde vive esa decision y donde se
 * prueba.
 */
export function CargaPage() {
  const q = useQuery({ queryKey: ["carga", "plantillas"], queryFn: fetchPlantillas })
  // `?plantilla=movimientos-bancarios`: quien llega desde la caja o desde el
  // maestro de movimientos aterriza ya en la plantilla correcta.
  const [params] = useSearchParams()
  const [elegida, setElegida] = React.useState<string | null>(() => params.get("plantilla"))

  const plantilla = q.data?.find((p) => p.id === elegida) ?? null

  return (
    <div className="space-y-3">
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !q.error && (q.data?.length ?? 0) === 0}
        emptyMessage={t("El backend no declara ninguna plantilla de carga.")}
        emptyHint={t("Sin plantillas no hay nada que subir. Es un fallo de configuración del servidor, no de tus datos.")}
        onRetry={() => void q.refetch()}
        columns={[30, 70]}
      >
        {q.data ? (
          <>
            <Selector plantillas={q.data} elegida={elegida} onElegir={setElegida} />
            {plantilla ? (
              <Carga key={plantilla.id} plantilla={plantilla} />
            ) : (
              <p className="px-1 text-2xs text-muted-foreground">
                {t("Elige arriba qué se va a cargar. Cada plantilla trae su propio archivo de ejemplo, generado de la misma declaración que después valida lo que subas.")}</p>
            )}
          </>
        ) : null}
      </DataState>
    </div>
  )
}

function Selector({
  plantillas,
  elegida,
  onElegir,
}: {
  plantillas: Plantilla[]
  elegida: string | null
  onElegir: (id: string) => void
}) {
  return (
    <WorkPanel title={t("Qué se va a cargar")} bodyClassName="p-2">
      <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {plantillas.map((p) => {
          const activa = p.id === elegida
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onElegir(p.id)}
              aria-pressed={activa}
              className={cn(
                "rounded-md border bg-surface px-2.5 py-2 text-left transition-colors duration-100",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                activa
                  ? "border-brand bg-brand-soft/40"
                  : "hover:border-border-strong hover:bg-surface-sunken/50",
              )}
            >
              <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <FileSpreadsheet className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                {p.nombre}
              </span>
              <span className="mt-0.5 block text-2xs leading-4 text-muted-foreground">
                {p.descripcion}
              </span>
            </button>
          )
        })}
      </div>
    </WorkPanel>
  )
}

function Carga({ plantilla }: { plantilla: Plantilla }) {
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const { toast } = useToast()
  const qc = useQueryClient()

  const [archivo, setArchivo] = React.useState<File | null>(null)
  const [vista, setVista] = React.useState<Previsualizacion | null>(null)
  /** De QUE archivo es la previsualizacion que se está mostrando. */
  const [vistaDe, setVistaDe] = React.useState<string | null>(null)
  const [motivo, setMotivo] = React.useState("")
  const [continuar, setContinuar] = React.useState(false)
  const [resultado, setResultado] = React.useState<ResultadoCarga | null>(null)
  const [rechazo, setRechazo] = React.useState<ErrorDeFila[]>([])
  const entrada = React.useRef<HTMLInputElement>(null)

  const huella = huellaDeArchivo(archivo)

  /** Deja de valer todo lo que se sabía: era de OTRO archivo. */
  function olvidarLectura() {
    setVista(null)
    setVistaDe(null)
    setRechazo([])
    setContinuar(false)
  }

  function elegirArchivo(nuevo: File | null) {
    setArchivo(nuevo)
    olvidarLectura()
    // El resultado anterior tampoco: es de la carga de otro archivo, y dejarlo
    // en pantalla junto a uno nuevo se lee como si este ya hubiera entrado.
    setResultado(null)
  }

  const previa = useMutation({
    mutationFn: () => previsualizar(plantilla.id, archivo as File),
    onSuccess: (r) => {
      setVista(r)
      setVistaDe(huella)
    },
    onError: (e) => {
      setVista(null)
      setVistaDe(null)
      toast({
        variant: "destructive",
        title: "No se pudo leer el archivo",
        description: e instanceof Error ? e.message : undefined,
      })
    },
  })

  const escribir = useMutation({
    mutationFn: () => cargar(plantilla.id, archivo as File, motivo, continuar),
    onSuccess: (r) => {
      setResultado(r)
      setRechazo([])
      toast({ title: `${plantilla.nombre}: ${titularDelResultado(r)}` })
      // Lo cargado cambia media aplicación: maestros, tesorería, facturación y
      // los primeros pasos del onboarding. Invalidar de menos deja pantallas
      // mostrando el estado anterior como si fuera el actual.
      for (const clave of [
        "sociedades", "clients", "suppliers", "people", "bancos", "tipos-cambio",
        "invoices", "primeros-pasos", "obligaciones", "entities",
      ]) {
        void qc.invalidateQueries({ queryKey: [clave] })
      }
      // El archivo ya se aplicó: obligar a elegirlo de nuevo evita la segunda
      // pulsación sin querer, que con un maestro idempotente no duplica pero sí
      // vuelve a pisar lo que alguien haya corregido a mano entretanto.
      setArchivo(null)
      olvidarLectura()
      if (entrada.current) entrada.current.value = ""
      setMotivo("")
    },
    onError: (e) => {
      // El 422 de esta API trae las filas malas dentro del detalle. Sin
      // desempaquetarlo el usuario lee «Datos invalidos.» y pierde las líneas.
      const { errores } = erroresDelRechazo(e)
      setRechazo(errores)
      onWriteError(`el archivo de ${plantilla.nombre.toLowerCase()}`, "cargar")(e)
    },
  })

  const veredicto = puedeCargar({
    plantillaId: plantilla.id,
    archivo: huella,
    previsualizacionDe: vistaDe,
    previsualizacion: vista,
    motivo,
    continuarConErrores: continuar,
    puedeEditar,
  })

  return (
    <>
      <Columnas plantilla={plantilla} />

      <WorkPanel
        title={`Subir · ${plantilla.nombre}`}
        footnote={t("Previsualizar no escribe nada: se puede repetir las veces que haga falta. La carga es todo o nada salvo que marques «continuar con errores», y es idempotente sobre la clave de la plantilla, así que volver a subir el archivo corregido actualiza en vez de duplicar.")}
      >
        <div className="space-y-2.5 p-2.5">
          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-1">
              <Label htmlFor="archivo-carga">{t("Archivo Excel o CSV")}</Label>
              <Input
                id="archivo-carga"
                ref={entrada}
                type="file"
                accept=".xlsx,.csv,.tsv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => elegirArchivo(e.target.files?.[0] ?? null)}
                className="text-xs"
              />
              <p className="text-2xs text-muted-foreground">
                {t("Se acepta lo que Excel produce de verdad: punto y coma o coma, UTF-8 o latin-1, y «1.234,56». No hace falta convertir nada.")}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!archivo || previa.isPending}
              onClick={() => previa.mutate()}
            >
              <Eye />
              {previa.isPending ? "Leyendo…" : "Previsualizar"}
            </Button>
          </div>

          {vista && vistaDe === huella ? (
            <Vista vista={vista} plantilla={plantilla} />
          ) : archivo && !previa.isPending ? (
            <p className="rounded-md border border-dashed px-2.5 py-2 text-2xs text-muted-foreground">
              {t("Todavía no se ha leído este archivo. Previsualiza para ver qué se crearía, qué se actualizaría y qué filas están mal.")}</p>
          ) : null}

          {vista && vistaDe === huella && vista.errores.length > 0 ? (
            <label className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft/40 px-2.5 py-2">
              <Checkbox
                checked={continuar}
                onCheckedChange={(v) => setContinuar(v === true)}
                className="mt-0.5"
              />
              <span className="text-2xs leading-4 text-foreground">
                <strong className="font-medium">{t("Continuar con errores.")}</strong> {" "}{t("Se cargarán las")}{" "}{vista.filas_leidas}{" "}{t("filas válidas y se omitirán las")}{" "}
                {vista.errores.length}{" "}{t("con error. Lo normal en contabilidad es corregir el archivo y volver a subirlo: recargarlo actualiza, no duplica.")}</span>
            </label>
          ) : null}

          <div className="space-y-1">
            <Label htmlFor="motivo-carga">{t("Motivo *")}</Label>
            <Textarea
              id="motivo-carga"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={2}
              placeholder={t("Por qué se carga esto. Queda en la pista de auditoría con tu nombre y la fecha.")}
              className="text-xs"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!veredicto.listo || escribir.isPending}
              onClick={() => escribir.mutate()}
            >
              <Upload />
              {escribir.isPending ? "Cargando…" : "Cargar"}
            </Button>
            <span
              className={cn(
                "text-2xs leading-4",
                veredicto.listo ? "text-muted-foreground" : "text-warning",
              )}
            >
              {veredicto.listo ? veredicto.consecuencia : veredicto.impedimento}
            </span>
          </div>

          {rechazo.length > 0 ? (
            <ListaDeErrores
              errores={rechazo}
              titulo={t("El backend rechazó el archivo y NO se escribió nada")}
            />
          ) : null}

          {resultado ? <Resultado resultado={resultado} /> : null}
        </div>
      </WorkPanel>
    </>
  )
}

/** Qué columnas espera esta plantilla. Sale del backend, no de una lista de aquí. */
function Columnas({ plantilla }: { plantilla: Plantilla }) {
  const [abierto, setAbierto] = React.useState(false)
  const obligatorias = plantilla.columnas.filter((c) => c.obligatoria).length
  const { toast } = useToast()

  const bajar = useMutation({
    mutationFn: () => descargarEjemplo(plantilla.id),
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "No se pudo descargar el ejemplo",
        description: e instanceof Error ? e.message : undefined,
      }),
  })

  return (
    <WorkPanel
      title={plantilla.nombre}
      meta={
        <>
          <Badge variant="outline">{plantilla.columnas.length} columnas</Badge>
          <Badge variant="outline">{obligatorias} obligatorias</Badge>
          {plantilla.clave.length > 0 ? (
            <Badge variant="brand-soft" className="code">
              {t("clave:")}{" "}{plantilla.clave.join(" + ")}
            </Badge>
          ) : null}
        </>
      }
      actions={
        <>
          <Button size="xs" variant="ghost" onClick={() => setAbierto((v) => !v)}>
            {abierto ? "Ocultar columnas" : "Ver columnas"}
          </Button>
          <Button size="xs" variant="outline" disabled={bajar.isPending} onClick={() => bajar.mutate()}>
            <Download />
            {bajar.isPending ? "Descargando…" : "Descargar plantilla de Excel"}
          </Button>
        </>
      }
      footnote={
        plantilla.clave.length > 0
          ? `${plantilla.descripcion} Se identifica cada fila por ${plantilla.clave.join(" + ")}: volver a subir el archivo corregido ACTUALIZA esas filas en vez de duplicarlas.`
          : plantilla.descripcion
      }
    >
      {abierto ? (
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[12rem]">{t("Columna")}</TableHead>
              <TableHead className="w-20">{t("Tipo")}</TableHead>
              <TableHead className="w-24">{t("Obligatoria")}</TableHead>
              <TableHead>{t("Qué es, y qué otros nombres valen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plantilla.columnas.map((c) => (
              <TableRow key={c.nombre}>
                <TableCell className="code font-medium">{c.nombre}</TableCell>
                <TableCell className="text-muted-foreground">{c.tipo}</TableCell>
                <TableCell>
                  {c.obligatoria ? (
                    <Badge variant="warning-soft" estado>
                      obligatoria
                    </Badge>
                  ) : (
                    <span className="text-2xs text-muted-foreground">opcional</span>
                  )}
                </TableCell>
                <TableCell className="text-2xs leading-4 text-muted-foreground">
                  {c.ayuda}
                  {c.opciones ? (
                    <>
                      {c.ayuda ? " " : null}
                      {t("Admite:")}{" "}<span className="code">{c.opciones.join(", ")}</span>.
                    </>
                  ) : null}
                  {c.alias.length > 0 ? (
                    <>
                      {" "}
                      {t("También vale escribir la columna como")}{" "}
                      <span className="code">{c.alias.join(", ")}</span>.
                    </>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </WorkPanel>
  )
}

/** Qué pasaría. Nada de esto se ha escrito. */
function Vista({ vista, plantilla }: { vista: Previsualizacion; plantilla: Plantilla }) {
  const noReconocidas = plantilla.columnas
    .filter((c) => !c.obligatoria && !vista.columnas_reconocidas.includes(c.nombre))
    .map((c) => c.nombre)

  return (
    <div className="space-y-2 rounded-md border bg-surface-sunken/40 p-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={vista.se_escribiria ? "success-soft" : "warning-soft"} estado>
          {vista.se_escribiria ? (
            <CheckCircle2 />
          ) : (
            <AlertTriangle />
          )}
          {t("simulación · no se ha escrito nada")}</Badge>
        <Badge variant="outline">{vista.filas_leidas}{" "}{t("filas válidas")}</Badge>
        <Badge variant="outline">{vista.nuevas}{" "}{t("se crearían")}</Badge>
        <Badge variant="outline">{vista.existentes}{" "}{t("se actualizarían")}</Badge>
        {vista.errores.length > 0 ? (
          <Badge variant="destructive-soft" estado>
            {vista.errores.length}{" "}{t("con error")}</Badge>
        ) : null}
      </div>

      <p className="text-2xs leading-4 text-muted-foreground">
        {vista.nota}{" "}{t("Separador detectado:")}{" "}{nombreDelSeparador(vista.separador)}.
      </p>

      {vista.columnas_ignoradas.length > 0 ? (
        <p className="rounded-md border border-warning/30 bg-warning-soft/40 px-2 py-1.5 text-2xs leading-4 text-foreground">
          <strong className="font-medium">
            {vista.columnas_ignoradas.length}{" "}
            {vista.columnas_ignoradas.length === 1 ? "columna se ignora" : "columnas se ignoran"}:
          </strong>{" "}
          <span className="code">{vista.columnas_ignoradas.join(", ")}</span>{t(". No corresponden a ningún campo de esta plantilla ni a ninguno de sus alias. Lo que hayas puesto ahí no se va a guardar.")}</p>
      ) : null}

      {noReconocidas.length > 0 ? (
        <p className="text-2xs leading-4 text-muted-foreground">
          {t("El archivo no trae estas columnas opcionales:")}{" "}
          <span className="code">{noReconocidas.join(", ")}</span>{t(". Se cargará sin ellas.")}</p>
      ) : null}

      {vista.errores.length > 0 ? (
        <ListaDeErrores errores={vista.errores} titulo={t("Filas que no pasan")} />
      ) : null}
    </div>
  )
}

/** Los errores, con su línea. «El archivo no es válido» obliga a buscar. */
function ListaDeErrores({ errores, titulo }: { errores: ErrorDeFila[]; titulo: string }) {
  const { mostrados, ocultos } = primerosErrores(errores)

  return (
    <div className="overflow-hidden rounded-md border border-destructive/25">
      <p className="border-b border-destructive/25 bg-destructive-soft/50 px-2 py-1 text-2xs font-medium text-destructive">
        {titulo} · {errores.length}
      </p>
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{t("Línea")}</TableHead>
            <TableHead className="w-[10rem]">{t("Columna")}</TableHead>
            <TableHead>{t("Qué pasa")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {mostrados.map((e, i) => (
            <TableRow key={`${e.linea}-${e.columna ?? ""}-${i}`}>
              <TableCell className="code">{e.linea}</TableCell>
              <TableCell className="code text-muted-foreground">{e.columna ?? "—"}</TableCell>
              <TableCell className="text-2xs leading-4">{e.motivo}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {ocultos > 0 ? (
        <p className="border-t px-2 py-1 text-2xs text-muted-foreground">
          y {ocultos}{" "}{t("más. Suelen ser el mismo problema repetido: arregla los de arriba y vuelve a previsualizar.")}</p>
      ) : null}
    </div>
  )
}

/** Lo que entró, y lo que no. Todos los contadores que devolvió el backend. */
function Resultado({ resultado }: { resultado: ResultadoCarga }) {
  const lineas = lineasDelResultado(resultado)
  const omitidas = lineas.filter((l) => l.esOmision && l.cantidad > 0)

  return (
    <div className="space-y-1.5 rounded-md border border-success/25 bg-success-soft/30 p-2.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <CheckCircle2 className="h-3.5 w-3.5 text-success" />
        {titularDelResultado(resultado)}
      </p>
      <ul className="space-y-0.5">
        {lineas.map((l) => (
          <li
            key={l.clave}
            className={cn(
              "text-2xs leading-4",
              l.esOmision && l.cantidad > 0 ? "text-warning" : "text-muted-foreground",
            )}
          >
            <span className="code font-medium">{l.cantidad}</span> {l.etiqueta}
          </li>
        ))}
      </ul>
      {omitidas.length > 0 ? (
        <p className="text-2xs leading-4 text-warning">
          {t("Lo omitido NO está cargado y no hay nada que reintentar hasta resolver el motivo.")}</p>
      ) : null}
      {resultado.errores.length > 0 ? (
        <ListaDeErrores
          errores={resultado.errores}
          titulo={t("Filas omitidas por error (no se escribieron)")}
        />
      ) : null}
    </div>
  )
}
